import {useMemo} from 'react';

import {useStorageManager} from '@/hooks';

import {
  TRUST_ACCOUNT_ACCESS_SECTIONS,
  countByAccessSection,
  filterByAccessSection,
  resolveAccessFilter
} from '../utils';

import type {TrustAccountAccessFilter} from '../utils';
import type {FirmTrustAccountListItemDTO} from '@sollapay/types';

const STORAGE_KEY = 'trust-accounts:access-filter';

function isAccessFilter(value: unknown): value is TrustAccountAccessFilter {
  return value === 'all' || TRUST_ACCOUNT_ACCESS_SECTIONS.some(section => section === value);
}

/**
 * Narrows the list to the accounts the caller holds at one role, the trustee tab first. The
 * choice is remembered like the table/card view, and the filter is only offered when the
 * accounts span more than one role — otherwise a remembered choice is ignored, so it can't hide
 * accounts behind controls that aren't shown.
 */
export function useTrustAccountAccessFilter(accounts: FirmTrustAccountListItemDTO[]) {
  const [storedFilter, setFilter] = useStorageManager<TrustAccountAccessFilter>(
    STORAGE_KEY,
    'trustee',
    isAccessFilter
  );
  const counts = useMemo(() => countByAccessSection(accounts), [accounts]);
  const isOffered = TRUST_ACCOUNT_ACCESS_SECTIONS.filter(section => counts[section] > 0).length > 1;
  const filter = resolveAccessFilter(storedFilter, counts, isOffered);
  const filtered = useMemo(() => filterByAccessSection(accounts, filter), [accounts, filter]);

  return {filter, setFilter, counts, filtered, isOffered};
}
